import { Box, FormLabel, Slider, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ToggleWithText } from "components/toggleWithText";

import { renderActions, selectHeadlight } from "./renderSlice";

type HeadlightState = ReturnType<typeof selectHeadlight>;

function SliderRow({
    label,
    value,
    min,
    max,
    step,
    onChange,
}: {
    label: string;
    value: number;
    min: number;
    max: number;
    step: number;
    onChange: (value: number) => void;
}) {
    return (
        <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
                {label}
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
                <Slider
                    aria-label={label}
                    min={min}
                    max={max}
                    step={step}
                    value={value}
                    valueLabelDisplay="auto"
                    onChange={(_evt, next) => {
                        if (!Array.isArray(next)) {
                            onChange(next);
                        }
                    }}
                    sx={{ flex: 1 }}
                />
                <TextField
                    type="number"
                    size="small"
                    value={value}
                    inputProps={{ min, max, step }}
                    onChange={(e) => {
                        const next = (e.target as HTMLInputElement).valueAsNumber;
                        if (Number.isFinite(next)) {
                            onChange(Math.min(max, Math.max(min, next)));
                        }
                    }}
                    sx={{ width: 88 }}
                />
            </Box>
        </Box>
    );
}

/** Designer-tuning UI. Shown in Settings when `?debug=true` is on the URL. */
export function HeadlightSettings() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const headlight = useAppSelector(selectHeadlight);

    const set = (patch: Partial<HeadlightState>) => {
        dispatch(renderActions.setHeadlight(patch));
    };

    const setInnerCone = (innerConeDeg: number) => {
        set({
            innerConeDeg,
            outerConeDeg: Math.max(headlight.outerConeDeg, innerConeDeg),
        });
    };

    const setOuterCone = (outerConeDeg: number) => {
        set({
            outerConeDeg,
            innerConeDeg: Math.min(headlight.innerConeDeg, outerConeDeg),
        });
    };

    const setOffset = (index: 0 | 1 | 2, value: number) => {
        const offset: [number, number, number] = [headlight.offset[0], headlight.offset[1], headlight.offset[2]];
        offset[index] = value;
        set({ offset });
    };

    const setColor = (index: 0 | 1 | 2, value: number) => {
        const color: [number, number, number] = [headlight.color[0], headlight.color[1], headlight.color[2]];
        color[index] = value;
        set({ color });
    };

    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            <FormLabel sx={{ fontWeight: 600, color: "text.primary" }}>{t("headlight")}</FormLabel>
            <ToggleWithText
                checked={headlight.enabled}
                onChange={() => set({ enabled: !headlight.enabled })}
                label="headlightEnabled"
            />
            <SliderRow
                label={t("headlightIntensity")}
                value={headlight.intensity}
                min={0}
                max={8}
                step={0.05}
                onChange={(intensity) => set({ intensity })}
            />
            <SliderRow
                label={t("headlightInnerCone")}
                value={headlight.innerConeDeg}
                min={0}
                max={90}
                step={1}
                onChange={setInnerCone}
            />
            <SliderRow
                label={t("headlightOuterCone")}
                value={headlight.outerConeDeg}
                min={0}
                max={90}
                step={1}
                onChange={setOuterCone}
            />
            <SliderRow
                label={t("headlightRange")}
                value={headlight.range}
                min={0.1}
                max={200}
                step={0.5}
                onChange={(range) => set({ range })}
            />
            <SliderRow
                label={t("headlightYaw")}
                value={headlight.yawDeg}
                min={-90}
                max={90}
                step={1}
                onChange={(yawDeg) => set({ yawDeg })}
            />
            <SliderRow
                label={t("headlightPitch")}
                value={headlight.pitchDeg}
                min={-90}
                max={90}
                step={1}
                onChange={(pitchDeg) => set({ pitchDeg })}
            />
            <Typography variant="body2">{t("headlightOffset")}</Typography>
            <Box display="flex" gap={1}>
                {(["X", "Y", "Z"] as const).map((axis, index) => (
                    <TextField
                        key={axis}
                        label={axis}
                        type="number"
                        size="small"
                        value={headlight.offset[index]}
                        inputProps={{ step: 0.1 }}
                        onChange={(e) => {
                            const next = (e.target as HTMLInputElement).valueAsNumber;
                            if (Number.isFinite(next)) {
                                setOffset(index as 0 | 1 | 2, next);
                            }
                        }}
                    />
                ))}
            </Box>
            <SliderRow
                label={t("headlightColorR")}
                value={headlight.color[0]}
                min={0}
                max={1}
                step={0.01}
                onChange={(value) => setColor(0, value)}
            />
            <SliderRow
                label={t("headlightColorG")}
                value={headlight.color[1]}
                min={0}
                max={1}
                step={0.01}
                onChange={(value) => setColor(1, value)}
            />
            <SliderRow
                label={t("headlightColorB")}
                value={headlight.color[2]}
                min={0}
                max={1}
                step={0.01}
                onChange={(value) => setColor(2, value)}
            />
        </Box>
    );
}
